/**
 * Utility functions for date formatting, time-of-day detection, and byte size display.
 *
 * @module utils/i18n
 * @author Snowfly
 * @version 1.0.0
 */
import moment from "moment";

/**
 * Formats a given date value into a specific string format.
 *
 * This function accepts a date input and utilizes the Moment.js library
 * to convert the date into the 'MM-DD-YYYY hh:mm a' format.
 *
 * The resulting string represents the date as a two-digit month,
 * day, and year followed by a 12-hour clock time with an AM/PM indicator.
 *
 * @param {string|Date|number} dateValue - The input date which can be a string, Date object, or timestamp.
 * @returns {string} - The formatted date string in 'MM-DD-YYYY hh:mm a' format.
 */
export const  dateFormat = (dateValue) => {
    return moment(dateValue).format('MM-DD-YYYY hh:mm a');
}

/**
 * Determines the part of the day (e.g., Morning, Afternoon, Evening, Night) based on the provided
 * time or the current time if no date is specified.
 *
 * @param {Date|null} [date=null] - Optional date object to determine the time of day.
 *                                   If not provided, the current system time is used.
 * @returns {string} A string representing the part of the day:
 *                   - "Morning" for hours before 12 PM
 *                   - "Afternoon" for hours between 12 PM and 4 PM
 *                   - "Evening" for hours between 4 PM and 8 PM
 *                   - "Night" for hours between 8 PM and 11 PM
 */
export const dayTime = (date = null) => {
    let timeStr = '';
    // To find the relative time for greeting message in header
    const currentTime = date ?? new Date();
    if (currentTime.getHours() < 12) {
        timeStr = 'Morning';
    } else if (currentTime.getHours() < 16) {
        timeStr = 'Afternoon';
    } else if (currentTime.getHours() < 20) {
        timeStr = 'Evening';
    } else if (currentTime.getHours() < 23) {
        timeStr = 'Night';
    }

    return timeStr;
}

/**
 * Converts a numeric value representing a size in bytes into a human-readable
 * format with an appropriate unit (e.g., B, KB, MB, GB, etc.).
 *
 * The method uses base-2 conversions, dividing the value by 1024 iteratively
 * until it finds the optimal unit to represent the value in a concise and
 * meaningful way. The final value is formatted to include up to two significant
 * digits.
 *
 * @param {number} value - The size in bytes to format. Must be a non-negative value.
 * @returns {string} A string representing the size formatted in a human-readable format
 * with the appropriate unit.
 */
export const formatSizeInBytes = (value) => {
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let bytes = value,
        unitIndex = 0;

    while (bytes && (bytes > 1024)) {
        bytes /= 1024;
        unitIndex++;
    }/*
    let bytes = value,
        sizeUnit = 'B';
    if (bytes > 1024) {
        bytes = bytes / 1024;
        sizeUnit = 'KB';
    }
    if (bytes > 1024) {
        bytes = bytes / 1024;
        sizeUnit = 'MB';
    }
    if (bytes > 1024) {
        bytes = bytes / 1024;
        sizeUnit = 'GB';
    }
    if (bytes > 1024) {
        bytes = bytes / 1024;
        sizeUnit = 'TB';
    }*/

    return bytes.toLocaleString(undefined, {maximumSignificantDigits: 2})+units[unitIndex];//sizeUnit;
}
